import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { toast } from "sonner";
import { getDeviceFingerprint } from "@/lib/deviceId";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { applySession } = useApp();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/login"); return; }
    const sessionId = m[1];

    (async () => {
      try {
        // Pull saved signup intent + role (set right before redirect).
        const intent = localStorage.getItem("chambanow_signup_intent");
        const role = localStorage.getItem("chambanow_signup_role");
        const ref = localStorage.getItem("chambanow_signup_ref");
        const agreed = localStorage.getItem("chambanow_signup_agreed");
        const hirerSafetyAck = localStorage.getItem("chambanow_signup_hirer_safety_ack");
        const termsVersion = localStorage.getItem("chambanow_signup_terms_version");
        const nextPath = localStorage.getItem("chambanow_signup_next");
        localStorage.removeItem("chambanow_signup_intent");
        localStorage.removeItem("chambanow_signup_role");
        localStorage.removeItem("chambanow_signup_ref");
        localStorage.removeItem("chambanow_signup_agreed");
        localStorage.removeItem("chambanow_signup_hirer_safety_ack");
        localStorage.removeItem("chambanow_signup_terms_version");
        localStorage.removeItem("chambanow_signup_next");
        const payload = { session_id: sessionId };
        const sLang = (localStorage.getItem("chambanow_lang") || "").toLowerCase();
        if (sLang === "en" || sLang === "es") payload.lang = sLang;
        if (intent === "signup" && role) {
          payload.role = role;
          payload.intent = "signup";
        }
        if (agreed === "1") {
          payload.agreed_to_terms = true;
          payload.terms_version = termsVersion || "2026-07-02";
        }
        if (hirerSafetyAck === "1") payload.hirer_safety_ack = true;
        if (ref) payload.referral_code = ref;
        payload.device_fingerprint = getDeviceFingerprint();
        const r = await axios.post(`${API}/auth/session`, payload);
        const newUser = r?.data?.user;
        const newRole = newUser?.role;
        const isNewUser = Boolean(r?.data?.is_new_user);
        // Backend returns the field as `session_token` (NOT `token`). Capture it
        // and persist as a bearer so the session sticks on return visits even if
        // the cross-site cookie is blocked — fixes the login/dashboard auth loop.
        const token = r?.data?.session_token;
        // Attribute to an affiliate partner (e.g. Los Plebes) for Google signups.
        // Brand-new-account guard is enforced server-side.
        try {
          const affCode = localStorage.getItem("chambanow_affiliate");
          if (affCode && token) {
            await axios.post(`${API}/affiliate/claim`, { code: affCode },
              { headers: { Authorization: `Bearer ${token}` } });
          }
        } catch (e) { /* non-blocking */ }
        // Clear hash
        window.history.replaceState({}, document.title, window.location.pathname);
        // Persist token + user into app state (replaces the old cookie-only fetchMe).
        applySession(token, newUser);
        toast.success("Signed in with Google");
        // If a safe `next` was stashed before OAuth, honor it now.
        const safeNext =
          nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") && !nextPath.startsWith("/\\")
            ? nextPath
            : "";
        if (safeNext) {
          navigate(safeNext);
        } else if (isNewUser && newRole === "hirer") {
          // Brand-new Google hirer → guided onboarding wizard (parity with email signup).
          navigate("/get-started");
        } else {
          navigate(newRole === "hirer" ? "/my-jobs" : "/dashboard");
        }
      } catch (e) {
        toast.error("Google sign-in failed");
        navigate("/login");
      }
    })();
  }, [navigate, applySession]);

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <div className="card-brutal p-8">
        <div className="font-display text-2xl uppercase tracking-tight">SIGNING YOU IN…</div>
      </div>
    </div>
  );
}
